import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { Subscription, timer } from 'rxjs'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'

/**
 * @public
 */
export type ToastElement = NgElement & WithProperties<ToastComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-toast',
  templateUrl: './toast.component.html',
  styleUrls: [ './toast.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [ CommonModule ]
})
export class ToastComponent implements OnDestroy {

  private _autoCloseSubscription: Subscription
  private _autoClose: boolean

  @Input()
  @HostBinding()
  type: string

  @Input()
  @HostBinding()
  header: string

  @Input()
  @HostBinding()
  message: string

  @Input()
  @HostBinding()
  delay = 7000

  @Output() dismiss: EventEmitter<Event> = new EventEmitter()

  visible = true

  ngOnDestroy(): void {
    this._autoCloseSubscription?.unsubscribe()
  }

  /**
   * Lifecycle event for when the alert component is closed. When the alert is closed,
   * it will set the visibility of the component to false, thus preventing it from rendering.
   *
   * @param event  Event from the `onClick` of the close button
   */
  onDismiss(event: Event) {
    this.dismiss.emit(event)
    this.visible = false
  }

  /**
   * Conditional styling for the component. It will provide classes for styling the component
   * based on the value of `ToastComponent.type`.
   *
   * @return  Returns an Object containing the class name and a boolean value to determine
   *          if said class is enabled or not.
   */
  get style(): Record<string, boolean> {
    let value = this?.type?.toUpperCase()
    return {
      'toast fade': true,
      'show': this.visible,
      'toast-danger': value === 'DANGER',
      'toast-success': value === 'SUCCESS',
      'toast-info': value === 'INFO',
      'toast-warning': value === 'WARNING'
    }
  }

  @Input()
  @HostBinding()
  set autoClose(value: string | boolean) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    if (this._autoClose !== pendingValue) {
      this._autoClose = pendingValue

      this._autoCloseSubscription?.unsubscribe()

      if (this._autoClose) {
        this._autoCloseSubscription = timer(this.delay).subscribe(() => this.onDismiss(null))
      }
    }
  }

  get autoClose(): boolean {
    return this._autoClose
  }
}

/**
 * @public
 */
export const registerToastElement = registerCustomElementFactory(ToastComponent)
